/**
 * Work queue tools — the RPA orchestration primitive. A work queue is a
 * shared to-do list: systems (or these tools) enqueue items, desktop flows
 * or cloud flows dequeue and process them, with retries, requeues, SLAs, and
 * exception classes tracked per item. Backed by the documented Dataverse
 * workqueue/workqueueitem surface (see src/api/desktop-flows-api.ts).
 */

import { z } from "zod";
import type { Tool } from "@modelcontextprotocol/sdk/types.js";
import {
  WORK_QUEUE_ITEM_RESULTS,
  WORK_QUEUE_ITEM_STATE,
  WORK_QUEUE_ITEM_STATUS,
  WORK_QUEUE_STATUS,
  type DesktopFlowsApi,
  type WorkQueueItem,
  type WorkQueueItemResult,
} from "../api/desktop-flows-api.js";
import { GUID_PATTERN } from "../utils/security.js";
import { logger } from "../utils/logger.js";
import { ConfirmationRequiredError } from "../utils/errors.js";

const QUEUE_ID_DESCRIPTION = "The work queue ID (GUID) from list_work_queues";

// =============================================================================
// Tool definitions
// =============================================================================

export const listWorkQueuesTool: Tool = {
  name: "list_work_queues",
  description:
    "List work queues (shared to-do lists that coordinate RPA and cloud-flow processing) with status and retry/requeue policy.",
  inputSchema: {
    type: "object",
    properties: {
      top: { type: "number", minimum: 1, maximum: 250, description: "Maximum queues to return" },
    },
  },
};

export const getWorkQueueTool: Tool = {
  name: "get_work_queue",
  description:
    "Get one work queue's configuration and health: item counts by state (queued/processing/processed/on-hold/error).",
  inputSchema: {
    type: "object",
    properties: {
      queueId: { type: "string", description: QUEUE_ID_DESCRIPTION },
    },
    required: ["queueId"],
  },
};

export const createWorkQueueTool: Tool = {
  name: "create_work_queue",
  description:
    "Create a work queue. Optionally enforce a JSON schema on item inputs and set retry/requeue caps and a default item time-to-live.",
  inputSchema: {
    type: "object",
    properties: {
      name: { type: "string", description: "Display name for the queue" },
      workqueueKey: {
        type: "string",
        description: "Unique key for the queue (defaults to the name)",
      },
      itemMaxRetryCount: {
        type: "number",
        minimum: 0,
        maximum: 100,
        description: "Automatic retries per item on IT exceptions (platform default when omitted)",
      },
      itemMaxRequeueCount: {
        type: "number",
        minimum: 0,
        maximum: 100,
        description: "Times an item may be requeued (platform default when omitted)",
      },
      defaultItemTimeToLiveMinutes: {
        type: "number",
        minimum: 1,
        description: "Default item expiry in minutes",
      },
      inputSchema: {
        type: "object",
        description: "JSON schema validated against every item's input at enqueue time",
      },
    },
    required: ["name"],
  },
};

export const enqueueWorkQueueItemTool: Tool = {
  name: "enqueue_work_queue_item",
  description:
    "Add an item to a work queue for processing. Supports priority (1 = highest), delayed availability, expiry, and a per-queue dedup key.",
  inputSchema: {
    type: "object",
    properties: {
      queueId: { type: "string", description: QUEUE_ID_DESCRIPTION },
      name: { type: "string", description: "Item name (what this piece of work is)" },
      input: {
        type: "object",
        description: "Item payload as key/value pairs (max 1 MB; validated when the queue declares an input schema)",
      },
      priority: {
        type: "number",
        minimum: 1,
        description:
          "Relative processing priority, lower first. NOTE: Dataverse may override this on create (a submitted 1 was stored as the default 200 on a live tenant), so treat ordering as best-effort rather than guaranteed.",
      },
      expiryDate: { type: "string", description: "ISO timestamp after which the item expires" },
      delayUntil: {
        type: "string",
        description: "ISO timestamp before which the item cannot be dequeued",
      },
      uniqueIdByQueue: {
        type: "string",
        description: "Per-queue unique key to prevent duplicate items",
      },
    },
    required: ["queueId", "name"],
  },
};

export const listWorkQueueItemsTool: Tool = {
  name: "list_work_queue_items",
  description:
    "List a work queue's items (newest first), optionally filtered by state: queued, processing, processed, on_hold, or error.",
  inputSchema: {
    type: "object",
    properties: {
      queueId: { type: "string", description: QUEUE_ID_DESCRIPTION },
      state: {
        type: "string",
        enum: ["queued", "processing", "processed", "on_hold", "error"],
        description: "Only items in this state",
      },
      top: { type: "number", minimum: 1, maximum: 250, description: "Maximum items to return" },
    },
    required: ["queueId"],
  },
};

export const dequeueWorkQueueItemTool: Tool = {
  name: "dequeue_work_queue_item",
  description:
    "Atomically claim the next queued item (oldest first, honoring priority) — it flips to Processing and is returned with its input. Finish it with update_work_queue_item.",
  inputSchema: {
    type: "object",
    properties: {
      queueId: { type: "string", description: QUEUE_ID_DESCRIPTION },
    },
    required: ["queueId"],
  },
};

export const updateWorkQueueItemTool: Tool = {
  name: "update_work_queue_item",
  description:
    "Finish a claimed work-queue item: mark it processed, fail it with an exception class (business/IT/generic), or requeue it (optionally delayed).",
  inputSchema: {
    type: "object",
    properties: {
      itemId: { type: "string", description: "The work queue item ID (GUID)" },
      result: {
        type: "string",
        enum: Object.keys(WORK_QUEUE_ITEM_RESULTS),
        description:
          "processed = done; business_exception = bad data (no retry); it_exception = transient failure (retryable); generic_exception = other failure; requeue = back to the queue",
      },
      processingResult: {
        type: "string",
        description: "Free-text note recorded on the item (what happened)",
      },
      delayUntil: {
        type: "string",
        description: "Requeue only: ISO timestamp before which the item cannot be dequeued again",
      },
    },
    required: ["itemId", "result"],
  },
};

export const deleteWorkQueueTool: Tool = {
  name: "delete_work_queue",
  description:
    "Permanently delete a work queue and its items. Requires confirm=true.",
  inputSchema: {
    type: "object",
    properties: {
      queueId: { type: "string", description: QUEUE_ID_DESCRIPTION },
      confirm: { type: "boolean", description: "Set true to confirm permanent deletion" },
    },
    required: ["queueId"],
  },
};

export const workQueueTools: Tool[] = [
  listWorkQueuesTool,
  getWorkQueueTool,
  createWorkQueueTool,
  enqueueWorkQueueItemTool,
  listWorkQueueItemsTool,
  dequeueWorkQueueItemTool,
  updateWorkQueueItemTool,
  deleteWorkQueueTool,
];

// =============================================================================
// Input schemas
// =============================================================================

const guid = (label: string) => z.string().regex(GUID_PATTERN, `${label} must be a GUID`);

const listQueuesSchema = z.object({ top: z.number().int().min(1).max(250).optional() });
const queueIdSchema = z.object({ queueId: guid("queueId") });

const createQueueSchema = z.object({
  name: z.string().min(1),
  workqueueKey: z.string().min(1).optional(),
  itemMaxRetryCount: z.number().int().min(0).max(100).optional(),
  itemMaxRequeueCount: z.number().int().min(0).max(100).optional(),
  defaultItemTimeToLiveMinutes: z.number().int().min(1).optional(),
  inputSchema: z.record(z.string(), z.unknown()).optional(),
});

const enqueueSchema = z.object({
  queueId: guid("queueId"),
  name: z.string().min(1),
  input: z.record(z.string(), z.unknown()).optional(),
  priority: z.number().int().min(1).optional(),
  expiryDate: z.string().optional(),
  delayUntil: z.string().optional(),
  uniqueIdByQueue: z.string().optional(),
});

const ITEM_STATE_FILTERS: Record<string, number> = {
  queued: 0,
  processing: 1,
  processed: 2,
  on_hold: 3,
  error: 4,
};

const listItemsSchema = z.object({
  queueId: guid("queueId"),
  state: z.enum(["queued", "processing", "processed", "on_hold", "error"]).optional(),
  top: z.number().int().min(1).max(250).optional(),
});

const updateItemSchema = z.object({
  itemId: guid("itemId"),
  result: z.enum(Object.keys(WORK_QUEUE_ITEM_RESULTS) as [WorkQueueItemResult, ...WorkQueueItemResult[]]),
  processingResult: z.string().optional(),
  delayUntil: z.string().optional(),
});

const deleteQueueSchema = z.object({
  queueId: guid("queueId"),
  confirm: z.boolean().optional().default(false),
});

// =============================================================================
// Formatting
// =============================================================================

function describeItemStatus(item: WorkQueueItem): string {
  const status =
    item.statuscode !== undefined ? WORK_QUEUE_ITEM_STATUS[item.statuscode] : undefined;
  if (status) return status;
  const state = item.statecode !== undefined ? WORK_QUEUE_ITEM_STATE[item.statecode] : undefined;
  return state ?? "Unknown";
}

function formatItemLine(item: WorkQueueItem): string {
  const retries =
    (item.retrycount ?? 0) > 0 || (item.requeuecount ?? 0) > 0
      ? ` — retries ${item.retrycount ?? 0}, requeues ${item.requeuecount ?? 0}`
      : "";
  const note = item.processingresult ? ` — ${item.processingresult}` : "";
  return `  ${item.name ?? item.workqueueitemid} (${item.workqueueitemid}) — ${describeItemStatus(item)}${item.priority !== undefined ? ` — priority ${item.priority}` : ""}${retries}${note}`;
}

// =============================================================================
// Handlers
// =============================================================================

export async function handleListWorkQueues(api: DesktopFlowsApi, input: unknown): Promise<string> {
  const parsed = listQueuesSchema.parse(input);
  logger.info(parsed, "Executing list_work_queues");
  const queues = await api.listWorkQueues(parsed);
  if (queues.length === 0) {
    return [
      "No work queues in this environment.",
      "Work queues coordinate processing between systems and RPA: enqueue items here, have desktop flows (or cloud flows) dequeue and process them with retries and exception tracking. Create one with create_work_queue.",
    ].join("\n");
  }
  return [
    `Work queues (${queues.length}):`,
    ...queues.map((q) => {
      const status = q.statuscode !== undefined ? WORK_QUEUE_STATUS[q.statuscode] : "Unknown";
      return `  ${q.name ?? q.workqueueid} (${q.workqueueid}) — ${status ?? "Unknown"} — max retries ${q.itemmaxretrycount ?? "default"}, max requeues ${q.itemmaxrequeuecount ?? "default"}`;
    }),
    "",
    "Inspect one with get_work_queue (includes item counts by state).",
  ].join("\n");
}

export async function handleGetWorkQueue(api: DesktopFlowsApi, input: unknown): Promise<string> {
  const parsed = queueIdSchema.parse(input);
  logger.info({ queueId: parsed.queueId }, "Executing get_work_queue");
  const { queue, counts } = await api.getWorkQueue(parsed.queueId);
  const status = queue.statuscode !== undefined ? WORK_QUEUE_STATUS[queue.statuscode] : "Unknown";
  return [
    `Work queue: ${queue.name ?? queue.workqueueid} (${queue.workqueueid})`,
    `Status: ${status ?? "Unknown"}`,
    `Key: ${queue.workqueuekey ?? "—"}`,
    `Policy: max retries ${queue.itemmaxretrycount ?? "default"}, max requeues ${queue.itemmaxrequeuecount ?? "default"}${queue.defaultitemtimetoliveinminutes ? `, item TTL ${queue.defaultitemtimetoliveinminutes} min` : ""}`,
    queue.inputschematype === 1 ? "Input schema: JSON schema enforced at enqueue" : undefined,
    "",
    `Items: ${counts.queued} queued, ${counts.processing} processing, ${counts.processed} processed, ${counts.onHold} on hold, ${counts.error} in error`,
    counts.error > 0
      ? "Investigate failures with list_work_queue_items (state: error)."
      : undefined,
  ]
    .filter((l): l is string => l !== undefined)
    .join("\n");
}

export async function handleCreateWorkQueue(api: DesktopFlowsApi, input: unknown): Promise<string> {
  const parsed = createQueueSchema.parse(input);
  logger.info({ name: parsed.name }, "Executing create_work_queue");
  const result = await api.createWorkQueue(parsed);
  return [
    `Work queue "${parsed.name}" created${result.workqueueid ? ` (${result.workqueueid})` : ""}.`,
    "Add items with enqueue_work_queue_item; process them with dequeue_work_queue_item + update_work_queue_item, or point a desktop flow's 'Process work queue items' action at it.",
  ].join("\n");
}

export async function handleEnqueueWorkQueueItem(
  api: DesktopFlowsApi,
  input: unknown,
): Promise<string> {
  const parsed = enqueueSchema.parse(input);
  logger.info({ queueId: parsed.queueId, name: parsed.name }, "Executing enqueue_work_queue_item");
  const result = await api.enqueueWorkQueueItem(parsed);
  return `Item "${parsed.name}" enqueued${result.workqueueitemid ? ` (${result.workqueueitemid})` : ""}${parsed.delayUntil ? `, available after ${parsed.delayUntil}` : ""}.`;
}

export async function handleListWorkQueueItems(
  api: DesktopFlowsApi,
  input: unknown,
): Promise<string> {
  const parsed = listItemsSchema.parse(input);
  logger.info(parsed, "Executing list_work_queue_items");
  const items = await api.listWorkQueueItems({
    queueId: parsed.queueId,
    statecode: parsed.state !== undefined ? ITEM_STATE_FILTERS[parsed.state] : undefined,
    top: parsed.top,
  });
  if (items.length === 0) {
    return parsed.state
      ? `No ${parsed.state.replace("_", "-")} items in this queue.`
      : "No items in this queue.";
  }
  return [
    `Work queue items (${items.length}${parsed.state ? `, ${parsed.state.replace("_", "-")}` : ""}, newest first):`,
    ...items.map(formatItemLine),
  ].join("\n");
}

export async function handleDequeueWorkQueueItem(
  api: DesktopFlowsApi,
  input: unknown,
): Promise<string> {
  const parsed = queueIdSchema.parse(input);
  logger.info({ queueId: parsed.queueId }, "Executing dequeue_work_queue_item");
  const item = await api.dequeueWorkQueueItem(parsed.queueId);
  if (!item) {
    return "The queue has no items ready to process (empty, or remaining items are delayed/on hold).";
  }
  return [
    `Claimed item: ${item.name ?? item.workqueueitemid} (${item.workqueueitemid}) — now Processing.`,
    item.input ? `Input:\n${item.input}` : "Input: none.",
    "",
    "When done, call update_work_queue_item with result: processed (or an exception class / requeue).",
  ].join("\n");
}

export async function handleUpdateWorkQueueItem(
  api: DesktopFlowsApi,
  input: unknown,
): Promise<string> {
  const parsed = updateItemSchema.parse(input);
  logger.info({ itemId: parsed.itemId, result: parsed.result }, "Executing update_work_queue_item");
  await api.updateWorkQueueItem(parsed);
  const verb =
    parsed.result === "processed"
      ? "marked processed"
      : parsed.result === "requeue"
        ? `sent back to the queue${parsed.delayUntil ? ` (available after ${parsed.delayUntil})` : ""}`
        : `failed as ${parsed.result.replace("_", " ")}`;
  return `Item ${parsed.itemId} ${verb}.`;
}

export async function handleDeleteWorkQueue(api: DesktopFlowsApi, input: unknown): Promise<string> {
  const parsed = deleteQueueSchema.parse(input);
  if (!parsed.confirm) {
    throw new ConfirmationRequiredError(`Confirm permanent deletion of work queue ${parsed.queueId} and all its items? Set confirm=true to proceed.`);
  }
  logger.info({ queueId: parsed.queueId }, "Executing delete_work_queue");
  await api.deleteWorkQueue(parsed.queueId);
  return `Work queue ${parsed.queueId} permanently deleted.`;
}

export const workQueueHandlers = {
  list_work_queues: handleListWorkQueues,
  get_work_queue: handleGetWorkQueue,
  create_work_queue: handleCreateWorkQueue,
  enqueue_work_queue_item: handleEnqueueWorkQueueItem,
  list_work_queue_items: handleListWorkQueueItems,
  dequeue_work_queue_item: handleDequeueWorkQueueItem,
  update_work_queue_item: handleUpdateWorkQueueItem,
  delete_work_queue: handleDeleteWorkQueue,
} as const;
